import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import './PhoneInput.css';

const PHONE_COUNTRIES_RAW = [
  { code: 'CM', name: 'Cameroun', dial: '+237', flag: '🇨🇲', pattern: /^\d{9}$/, placeholder: '6XX XXX XXX' },
  { code: 'FR', name: 'France', dial: '+33', flag: '🇫🇷', pattern: /^\d{9}$/, placeholder: '6 XX XX XX XX' },
  { code: 'DE', name: 'Allemagne', dial: '+49', flag: '🇩🇪', pattern: /^\d{10,11}$/, placeholder: '1XX XXXXXXXX' },
  { code: 'CN', name: 'Chine', dial: '+86', flag: '🇨🇳', pattern: /^\d{11}$/, placeholder: '1XX XXXX XXXX' },
  { code: 'US', name: 'États-Unis', dial: '+1', flag: '🇺🇸', pattern: /^\d{10}$/, placeholder: 'XXX XXX XXXX' },
  { code: 'GB', name: 'Royaume-Uni', dial: '+44', flag: '🇬🇧', pattern: /^\d{10}$/, placeholder: '7XXX XXXXXX' },
  { code: 'SN', name: 'Sénégal', dial: '+221', flag: '🇸🇳', pattern: /^\d{9}$/, placeholder: '7X XXX XX XX' },
  { code: 'CI', name: "Côte d'Ivoire", dial: '+225', flag: '🇨🇮', pattern: /^\d{8,10}$/, placeholder: '07 XX XX XX XX' },
  { code: 'GA', name: 'Gabon', dial: '+241', flag: '🇬🇦', pattern: /^\d{7,8}$/, placeholder: 'XX XX XX XX' },
  { code: 'CG', name: 'Congo', dial: '+242', flag: '🇨🇬', pattern: /^\d{9}$/, placeholder: 'XX XXX XXXX' },
  { code: 'CD', name: 'RD Congo', dial: '+243', flag: '🇨🇩', pattern: /^\d{9}$/, placeholder: 'XX XXX XXXX' },
  { code: 'BJ', name: 'Bénin', dial: '+229', flag: '🇧🇯', pattern: /^\d{8}$/, placeholder: 'XX XX XX XX' },
  { code: 'TG', name: 'Togo', dial: '+228', flag: '🇹🇬', pattern: /^\d{8}$/, placeholder: 'XX XX XX XX' },
  { code: 'ML', name: 'Mali', dial: '+223', flag: '🇲🇱', pattern: /^\d{8}$/, placeholder: 'XX XX XX XX' },
  { code: 'GN', name: 'Guinée', dial: '+224', flag: '🇬🇳', pattern: /^\d{9}$/, placeholder: 'XXX XX XX XX' },
  { code: 'BF', name: 'Burkina Faso', dial: '+226', flag: '🇧🇫', pattern: /^\d{8}$/, placeholder: 'XX XX XX XX' },
  { code: 'NE', name: 'Niger', dial: '+227', flag: '🇳🇪', pattern: /^\d{8}$/, placeholder: 'XX XX XX XX' },
  { code: 'TD', name: 'Tchad', dial: '+235', flag: '🇹🇩', pattern: /^\d{8}$/, placeholder: 'XX XX XX XX' },
  { code: 'MG', name: 'Madagascar', dial: '+261', flag: '🇲🇬', pattern: /^\d{9}$/, placeholder: '3X XX XXX XX' },
  { code: 'RW', name: 'Rwanda', dial: '+250', flag: '🇷🇼', pattern: /^\d{9}$/, placeholder: '7XX XXX XXX' },
  { code: 'MR', name: 'Mauritanie', dial: '+222', flag: '🇲🇷', pattern: /^\d{8}$/, placeholder: 'XX XX XX XX' },
  { code: 'MA', name: 'Maroc', dial: '+212', flag: '🇲🇦', pattern: /^\d{9}$/, placeholder: '6XX XX XX XX' },
  { code: 'TN', name: 'Tunisie', dial: '+216', flag: '🇹🇳', pattern: /^\d{8}$/, placeholder: 'XX XXX XXX' },
  { code: 'DZ', name: 'Algérie', dial: '+213', flag: '🇩🇿', pattern: /^\d{9}$/, placeholder: '5XX XX XX XX' },
  { code: 'BE', name: 'Belgique', dial: '+32', flag: '🇧🇪', pattern: /^\d{8,9}$/, placeholder: '4XX XX XX XX' },
  { code: 'CH', name: 'Suisse', dial: '+41', flag: '🇨🇭', pattern: /^\d{9}$/, placeholder: '7X XXX XX XX' },
  { code: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦', pattern: /^\d{10}$/, placeholder: 'XXX XXX XXXX' },
  { code: 'GH', name: 'Ghana', dial: '+233', flag: '🇬🇭', pattern: /^\d{9}$/, placeholder: 'XX XXX XXXX' },
  { code: 'NG', name: 'Nigeria', dial: '+234', flag: '🇳🇬', pattern: /^\d{10}$/, placeholder: 'XXX XXX XXXX' },
];

export const PHONE_COUNTRIES = [...PHONE_COUNTRIES_RAW].sort((a, b) =>
  a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }),
);

const COUNTRY_CODES = new Set(PHONE_COUNTRIES_RAW.map((country) => country.code));

function normalizeCountryCode(countryCode) {
  const normalized = countryCode?.trim().toUpperCase();
  return normalized && COUNTRY_CODES.has(normalized) ? normalized : undefined;
}

function detectPhoneCountryFromTimezone(timezone) {
  const tz = timezone?.trim();
  if (!tz) return undefined;

  const direct = {
    'Africa/Abidjan': 'CI',
    'Africa/Douala': 'CM',
    'Africa/Dakar': 'SN',
    'Africa/Lagos': 'NG',
    'Europe/Paris': 'FR',
    'Europe/Brussels': 'BE',
    'Europe/London': 'GB',
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Los_Angeles': 'US',
    'America/Toronto': 'CA',
  };
  const directMatch = normalizeCountryCode(direct[tz]);
  if (directMatch) return directMatch;

  if (tz.startsWith('Europe/')) return 'FR';
  if (tz.startsWith('America/')) return 'US';
  if (tz.startsWith('Africa/')) return 'CI';
  return undefined;
}

function detectPhoneCountryFromBrowser() {
  if (typeof Intl !== 'undefined') {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneCountry = detectPhoneCountryFromTimezone(timezone);
    if (timezoneCountry) return timezoneCountry;
  }

  if (typeof navigator !== 'undefined') {
    const languages = [...(navigator.languages || []), navigator.language].filter(Boolean);
    for (const language of languages) {
      const region = language.split('-')[1];
      const localeCountry = normalizeCountryCode(region);
      if (localeCountry) return localeCountry;
    }
  }

  return 'CM';
}

function parsePhoneValue(value) {
  if (!value) return { countryCode: '', nationalNumber: '' };

  const cleaned = value.replace(/\s+/g, '');
  const sorted = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (cleaned.startsWith(c.dial)) {
      return { countryCode: c.code, nationalNumber: cleaned.slice(c.dial.length) };
    }
  }
  if (cleaned.startsWith('+')) {
    return { countryCode: '', nationalNumber: cleaned };
  }
  return { countryCode: '', nationalNumber: cleaned };
}

export function isPhoneNumberComplete(value) {
  const parsed = parsePhoneValue(value);
  if (!parsed.nationalNumber.trim()) return false;

  const country = PHONE_COUNTRIES.find((c) => c.code === parsed.countryCode)
    || PHONE_COUNTRIES.find((c) => value.includes(c.dial));
  if (!country) {
    const digits = parsed.nationalNumber.replace(/\s/g, '');
    return digits.length >= 7;
  }

  const digits = parsed.nationalNumber.replace(/\s/g, '');
  return country.pattern ? country.pattern.test(digits) : digits.length >= 7;
}

export const PhoneInput = React.forwardRef(
  (
    {
      value = '',
      onChange,
      defaultCountry,
      disabled = false,
      error = false,
      errorMessage,
      fullWidth = false,
      placeholder,
      className = '',
      id,
      required = false,
      onCountryChange,
    },
    ref,
  ) => {
    const parsed = useMemo(() => parsePhoneValue(value), [value]);
    const resolvedDefaultCountry = useMemo(
      () => normalizeCountryCode(defaultCountry) || detectPhoneCountryFromBrowser(),
      [defaultCountry],
    );
    const countryTouchedRef = useRef(false);
    const [selectedCountry, setSelectedCountry] = useState(
      parsed.nationalNumber && parsed.countryCode ? parsed.countryCode : resolvedDefaultCountry,
    );
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);
    const searchRef = useRef(null);

    const country = useMemo(
      () => PHONE_COUNTRIES.find((c) => c.code === selectedCountry) || PHONE_COUNTRIES[0],
      [selectedCountry],
    );

    const nationalNumber = useMemo(() => {
      const cleaned = value.replace(/\s+/g, '');
      if (cleaned.startsWith(country.dial)) {
        return cleaned.slice(country.dial.length);
      }
      if (!value.trim()) return '';
      if (parsed.nationalNumber) return parsed.nationalNumber;
      return cleaned.replace(/^\+/, '');
    }, [value, country.dial, parsed.nationalNumber]);

    useEffect(() => {
      if (parsed.nationalNumber && parsed.countryCode && parsed.countryCode !== selectedCountry) {
        setSelectedCountry(parsed.countryCode);
        countryTouchedRef.current = true;
      }
    }, [parsed.countryCode, parsed.nationalNumber, selectedCountry]);

    useEffect(() => {
      if (countryTouchedRef.current || parsed.nationalNumber) return;
      if (selectedCountry === resolvedDefaultCountry) return;
      setSelectedCountry(resolvedDefaultCountry);
    }, [parsed.nationalNumber, resolvedDefaultCountry, selectedCountry]);

    useEffect(() => {
      if (!dropdownOpen) return undefined;
      const handler = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
          setDropdownOpen(false);
          setSearchTerm('');
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [dropdownOpen]);

    useEffect(() => {
      if (dropdownOpen && searchRef.current) {
        searchRef.current.focus();
      }
    }, [dropdownOpen]);

    const handleCountrySelect = useCallback(
      (countryCode) => {
        countryTouchedRef.current = true;
        setSelectedCountry(countryCode);
        setDropdownOpen(false);
        setSearchTerm('');
        onCountryChange?.(countryCode);
        const newCountry = PHONE_COUNTRIES.find((c) => c.code === countryCode);
        if (newCountry) {
          onChange(
            nationalNumber ? `${newCountry.dial} ${nationalNumber}` : `${newCountry.dial} `,
          );
        }
      },
      [nationalNumber, onChange, onCountryChange],
    );

    const handleNumberChange = useCallback(
      (e) => {
        const raw = e.target.value.replace(/[^\d\s]/g, '');
        onChange(`${country.dial} ${raw}`);
      },
      [country.dial, onChange],
    );

    const filteredCountries = useMemo(() => {
      if (!searchTerm) return PHONE_COUNTRIES;
      const q = searchTerm.toLowerCase();
      return PHONE_COUNTRIES.filter(
        (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q),
      );
    }, [searchTerm]);

    const isValid = useMemo(() => {
      if (!nationalNumber) return true;
      const digits = nationalNumber.replace(/\s/g, '');
      return country.pattern ? country.pattern.test(digits) : digits.length >= 7;
    }, [nationalNumber, country]);

    const inputId = id || `phone-${Math.random().toString(36).substr(2, 9)}`;

    const wrapperClasses = [
      'lwz-phone-input',
      fullWidth ? 'lwz-phone-input--full' : '',
      error || (!isValid && nationalNumber) ? 'lwz-phone-input--error' : '',
      disabled ? 'lwz-phone-input--disabled' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses} ref={wrapperRef}>
        <div className="lwz-phone-input__container">
          <button
            type="button"
            className="lwz-phone-input__country-btn"
            onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}
            disabled={disabled}
            aria-label="Sélectionner le pays"
            aria-expanded={dropdownOpen}
          >
            <span className="lwz-phone-input__flag">{country.flag}</span>
            <span className="lwz-phone-input__dial">{country.dial}</span>
            <span className="lwz-phone-input__chevron">{dropdownOpen ? '▲' : '▼'}</span>
          </button>

          <input
            ref={ref}
            id={inputId}
            className="lwz-phone-input__input"
            type="tel"
            value={nationalNumber}
            onChange={handleNumberChange}
            placeholder={placeholder || country.placeholder || 'Numéro de téléphone'}
            disabled={disabled}
            required={required}
            aria-invalid={error || (!isValid && !!nationalNumber)}
            autoComplete="tel"
          />
        </div>

        {dropdownOpen && (
          <div className="lwz-phone-input__dropdown">
            <div className="lwz-phone-input__search-wrapper">
              <input
                ref={searchRef}
                className="lwz-phone-input__search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un pays..."
                autoComplete="off"
              />
            </div>
            <ul className="lwz-phone-input__list" role="listbox">
              {filteredCountries.map((c) => (
                <li
                  key={c.code}
                  className={`lwz-phone-input__option ${c.code === selectedCountry ? 'lwz-phone-input__option--selected' : ''}`}
                  role="option"
                  aria-selected={c.code === selectedCountry}
                  onClick={() => handleCountrySelect(c.code)}
                >
                  <span className="lwz-phone-input__option-flag">{c.flag}</span>
                  <span className="lwz-phone-input__option-name">{c.name}</span>
                  <span className="lwz-phone-input__option-dial">{c.dial}</span>
                </li>
              ))}
              {filteredCountries.length === 0 && (
                <li className="lwz-phone-input__no-results">Aucun pays trouvé</li>
              )}
            </ul>
          </div>
        )}

        {error && errorMessage && (
          <span className="lwz-phone-input__error" role="alert">
            {errorMessage}
          </span>
        )}
        {!isValid && nationalNumber && !error && (
          <span className="lwz-phone-input__error" role="alert">
            Numéro invalide pour {country.name}
          </span>
        )}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
